import { useQuery } from '@tanstack/react-query';

import { operations, type OperationEngineRun } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';

/**
 * Aba "Historico do motor" (COT-032 e COT-086): as execucoes do motor de credito da
 * operacao, da mais recente para a mais antiga, sobre a rota que o motor ja tinha.
 *
 * O 44 nao leu as colunas desta aba no OnPoint (A CONFIRMAR); a tela mostra o que a
 * execucao imutavel guarda: quando, modo, resultado final e calculado, pendencias,
 * versao dos parametros e quem pediu. A leitura pede a permissao do motor.
 */
export function EngineHistoryTab({ operationId }: { operationId: string }) {
  const { can } = useAuth();
  const pode = can('credito.motor.view');

  const consulta = useQuery({
    queryKey: ['operations', operationId, 'engine-runs'],
    queryFn: ({ signal }) => operations.engineRuns(operationId, signal),
    enabled: pode,
  });

  return (
    <Card>
      <CardHeader
        title="Historico do motor"
        description="Execucoes do motor de credito desta cotacao. Cada execucao e gravada uma vez e nao muda."
      />
      <CardBody className="flex flex-col gap-4">
        {!pode ? (
          <p className="text-sm text-ink-500">
            O historico do motor pede a permissao de ver o motor de credito.
          </p>
        ) : consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar o historico do motor">
            {consulta.error.message}
          </Alert>
        ) : (
          <DataTable
            columns={[
              {
                key: 'data',
                header: 'Executada em',
                render: (r: OperationEngineRun) => formatDateTime(r.created_at),
              },
              { key: 'modo', header: 'Modo', render: (r) => r.mode },
              { key: 'resultado', header: 'Resultado', render: (r) => r.final_result },
              {
                key: 'calculado',
                header: 'Resultado calculado',
                hideBelow: 'md',
                render: (r) => r.calculated_result,
              },
              {
                key: 'pendencias',
                header: 'Pendencias',
                align: 'right',
                render: (r) => String(r.pendency_count),
              },
              {
                key: 'versao',
                header: 'Versao dos parametros',
                hideBelow: 'lg',
                render: (r) =>
                  r.parameter_set ? `v${r.parameter_set.version}` : r.engine_version,
              },
              {
                key: 'quem',
                header: 'Solicitado por',
                hideBelow: 'lg',
                render: (r) => r.requested_label || '-',
              },
              {
                key: 'simulada',
                header: 'Simulada',
                render: (r) =>
                  r.simulated ? <Badge tone="warning">SIMULADO</Badge> : 'Nao',
              },
            ]}
            items={consulta.data ?? []}
            keyOf={(r) => r.id}
            loading={consulta.isPending}
            empty={
              <EmptyState
                icon="refresh"
                title="Nenhuma execucao do motor nesta cotacao"
                description="O motor roda pela analise de credito da operacao."
              />
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
